import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { ClipboardCheck, Clock, AlertCircle, CheckCircle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function DeliveryApplicationStatus() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [status, setStatus] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    const checkStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('delivery_partners')
          .select('status, rejection_reason')
          .eq('id', user.id)
          .maybeSingle()

        if (error) throw error
        if (!data) {
          navigate('/become-delivery')
          return
        }

        setStatus(data.status)

        // If status is active, update user role and redirect to dashboard
        if (data.status === 'active') {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ role: 'delivery' })
            .eq('id', user.id)

          if (updateError) throw updateError
          
          // Redirect to dashboard
          navigate('/delivery/dashboard')
          return
        }
      } catch (err) {
        console.error('Error checking status:', err)
      } finally {
        setLoading(false)
      }
    }

    checkStatus()
  }, [user, navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Don't render anything if status is active (will redirect)
  if (status === 'active') {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            {status === 'pending' && (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Demande en cours d'analyse
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Votre demande est en cours d'examen. Nous vous contacterons dans les 48 heures.
                </p>
                <div className="mt-8 space-y-4">
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ClipboardCheck className="h-5 w-5 text-yellow-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Prochaines étapes :
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Vérification des documents</li>
                            <li>Validation des informations</li>
                            <li>Approbation finale</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {status === 'rejected' && (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Demande refusée
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Malheureusement, votre demande n'a pas été approuvée. Vous pouvez soumettre une nouvelle demande après 30 jours.
                </p>
                <div className="mt-6">
                  <Button
                    variant="primary"
                    onClick={() => navigate('/')}
                  >
                    Retour à l'accueil
                  </Button>
                </div>
              </div>
            )}

            {status === 'blocked' && (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Compte bloqué
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Votre compte livreur a été bloqué. Veuillez contacter le support pour plus d'informations.
                </p>
                <div className="mt-6">
                  <Button
                    variant="primary"
                    onClick={() => navigate('/support')}
                  >
                    Contacter le support
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}